/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import styles from './style.module.scss';
import { Actionable, Text, View } from '@az/starc-ui';
import { Rating } from './Rating/Rating';
import { Author } from './Author/Author';
import { Content } from './Content/Content';
import { PurchasedFor } from './PurchasedFor/PurchasedFor';
import { Syndicated } from './Badges/Syndicated';
import { Response } from './Response/Response';
import { SecondaryRatingsList } from './SecondaryRating/SecondaryRatingsList';
import { ReviewFeedbackActions } from '@/features/reviews/components/ReviewFeedbackActions/ReviewFeedbackActions';
import { useMemo, useState } from 'react';
import { VerifiedPurchase } from './Badges/VerifiedPurchase';
import { Recommended } from './Badges/Recommended';
import { Sweepstakes } from './Badges/Sweepstakes';
import type { ReviewType } from '../../interface/types';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { findBadgeById } from '../../utils/findBadgeById';
import NextImage from '@/components/NextImage';
import React from 'react';
import { ReviewImageDetailsModal } from '../ReviewImages/ReviewImageDetailsModal/ReviewImageDetailsModal';

type ReviewProps = {
  review: ReviewType;
  detailsModal?: boolean;
};

type ImageProps = {
  photoUrls: string[];
  reviews?: ReviewType[] | null | undefined | ReviewType;
  photoCaptions: string[] | undefined | null;
  renderReview: (reviewData: ReviewType, detailsModal: boolean) => JSX.Element;
};

const ReviewImages = ({ photoUrls, photoCaptions, reviews, renderReview }: ImageProps) => {
  const [isDetailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const handleDetailsModalState: React.Dispatch<React.SetStateAction<boolean>> = (data) => {
    setDetailsModalOpen(data);
  };
  return (
    <View className={styles.imagesContainer}>
      {photoUrls.map((image, index) => (
        <Actionable
          key={index}
          onClick={() => {
            setSelectedImageIndex(index);
            setDetailsModalOpen(true);
          }}
        >
          <NextImage
            src={photoUrls?.[index]}
            alt={photoCaptions?.[index] || 'Image'}
            width={100}
            height={100}
            className={styles.images}
          />
        </Actionable>
      ))}
      {isDetailsModalOpen && (
        <ReviewImageDetailsModal
          isReviewDetailsModalOpen={isDetailsModalOpen}
          handleReviewDetailsModalOpen={handleDetailsModalState}
          images={photoUrls}
          photoCaptions={photoCaptions}
          imageIndex={selectedImageIndex}
          setImageIndex={setSelectedImageIndex}
          reviews={reviews}
          renderReview={renderReview}
        />
      )}
    </View>
  );
};

export const Review = ({ review, detailsModal }: ReviewProps) => {
  const {
    reviewId,
    reviewRating,
    reviewTitle,
    reviewerNickname,
    reviewText,
    reviewerVehicle,
    recommendedFlag,
    totalNegativeFeedbackCount,
    totalPositiveFeedbackCount,
    reviewSubmissionTimestamp,
    reviewPhotos,
    reviewResponseText,
    reviewResponseTeamName,
    reviewResponseSubmissionTimestamp,
    reviewSecondaryRatings,
    reviewBadges,
  } = review;
  const isMobile = useMediaQuery((theme) => theme.breakpoints.only('sm'));
  const extractPhotoUrls = (photos: ReviewType['reviewPhotos']) => {
    return (
      photos
        ?.map((photo) => {
          const normalSize = photo.photoSizes.find((size) => size.photoSizeId === 'normal');
          return normalSize?.photoUrl;
        })
        .filter((url): url is string => url !== undefined) ?? []
    );
  };

  const reviewCaptions = reviewPhotos?.map((review) => review.photoCaption);

  const showPreviewImages = (reviewPhotos?.length ?? 0) && !detailsModal;

  const verifiedPurchaseBadge = useMemo(
    () => findBadgeById(reviewBadges, 'verifiedPurchaser'),
    [reviewBadges]
  );

  const sweepstakesBadge = useMemo(
    () => findBadgeById(reviewBadges, 'incentivizedReview'),
    [reviewBadges]
  );

  const syndicatedBadge = useMemo(() => findBadgeById(reviewBadges, 'Syndicated'), [reviewBadges]);

  const hasBadges = Boolean(verifiedPurchaseBadge || recommendedFlag || sweepstakesBadge);

  const hasResponseRequiredFields =
    reviewResponseText && reviewResponseTeamName && reviewResponseSubmissionTimestamp;

  const renderReview = (reviewData: ReviewType, detailsModal: boolean) => {
    return <Review review={reviewData} detailsModal={detailsModal} />;
  };

  return (
    <View className={styles.reviewContainer}>
      <View className={styles.reviewHeader}>
        <Rating stars={reviewRating} />
        {reviewTitle ? (
          <Text as="h3" variant="main-body" weight="bold" className={styles.title}>
            {reviewTitle}
          </Text>
        ) : null}
        <Author date={reviewSubmissionTimestamp} nickname={reviewerNickname} />
        {hasBadges ? (
          <View className={styles.badgesContainer}>
            {verifiedPurchaseBadge ? <VerifiedPurchase /> : null}
            {recommendedFlag ? <Recommended /> : null}
            {sweepstakesBadge ? <Sweepstakes /> : null}
          </View>
        ) : null}
      </View>

      {showPreviewImages && isMobile ? (
        <ReviewImages
          photoUrls={extractPhotoUrls(reviewPhotos)}
          photoCaptions={reviewCaptions}
          reviews={review}
          renderReview={renderReview}
        />
      ) : null}
      {reviewText ? <Content text={reviewText} detailsModal={detailsModal} /> : null}
      {showPreviewImages && !isMobile ? (
        <ReviewImages
          photoUrls={extractPhotoUrls(reviewPhotos)}
          photoCaptions={reviewCaptions}
          reviews={review}
          renderReview={renderReview}
        />
      ) : null}
      <View className={styles.reviewDetails}>
        {reviewerVehicle ? <PurchasedFor vehicle={reviewerVehicle} /> : null}
        {!syndicatedBadge ? (
          <ReviewFeedbackActions
            reviewId={reviewId}
            positiveFeedbackCount={totalPositiveFeedbackCount}
            negativeFeedbackCount={totalNegativeFeedbackCount}
          />
        ) : null}
      </View>
      {!detailsModal && reviewSecondaryRatings.length > 0 ? (
        <SecondaryRatingsList list={reviewSecondaryRatings} />
      ) : null}
      {syndicatedBadge ? <Syndicated {...syndicatedBadge} /> : null}
      {hasResponseRequiredFields ? (
        <Response
          date={reviewResponseSubmissionTimestamp}
          team={reviewResponseTeamName}
          text={reviewResponseText}
        />
      ) : null}
    </View>
  );
};
